import type {
  Academy,
  ApiResponse,
  ClubOperatingHourSlot,
  Location,
  OrgAmenityKey,
  RacquetSportId,
} from '@court-sync/shared';
import { publicApiGet } from './apiClient';

export interface PublicOrgModules {
  hasAcademy: boolean;
  hasClub: boolean;
  courtHireEntitled: boolean;
  membershipsEntitled: boolean;
  membershipOnlineJoinEnabled: boolean;
  playEventsEntitled: boolean;
}

export interface PublicOrgSport {
  sportId: RacquetSportId;
  customLabel?: string | null;
  isPrimary: boolean;
}

export interface PublicOrgOrganization {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  galleryUrls?: string[];
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  facebook?: string | null;
  instagram?: string | null;
  twitter?: string | null;
  linkedin?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  country?: string | null;
  region?: string | null;
  stateOrProvince?: string | null;
  timezone?: string | null;
  googleMapsPlaceId?: string | null;
  googleMapsAddress?: string | null;
  showCoachesOnPublicPage?: boolean;
  websiteEmbedEnabled?: boolean;
  subdomain?: string | null;
  subdomainEnabled?: boolean;
}

export interface PublicOrganizationProfile {
  organization: PublicOrgOrganization;
  modules: PublicOrgModules;
  academyName: string | null;
  clubName: string | null;
  academy: Academy | null;
  academyId: string | null;
  academySlug: string | null;
  clubId: string | null;
  clubSlug: string | null;
  amenities: OrgAmenityKey[];
  sports: PublicOrgSport[];
  locations: Location[];
  operatingHours: ClubOperatingHourSlot[] | null;
}

export interface PublicDirectoryEntry {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  country: string | null;
  stateOrProvince: string | null;
  suburb: string | null;
  sportIds: string[];
}

export type CourtHireSlotState = 'available' | 'unavailable' | 'booked';

export interface CourtHireAvailabilitySlot {
  startTime: string;
  endTime: string;
  state: CourtHireSlotState;
}

export interface CourtHireAvailabilityCourtRow {
  id: string;
  name: string;
  locationId?: string | null;
  slots: CourtHireAvailabilitySlot[];
}

export interface CourtHireAvailabilityGrid {
  date: string;
  slotDurationMinutes: number;
  operatingWindow: { startTime: string; endTime: string };
  courts: CourtHireAvailabilityCourtRow[];
}

export interface CourtHireAvailabilityQuery {
  date: string;
  sportId?: string;
  locationId?: string;
}

export const publicOrgService = {
  async getProfile(orgSlug: string): Promise<ApiResponse<PublicOrganizationProfile>> {
    return publicApiGet<PublicOrganizationProfile>(
      `/public/orgs/${encodeURIComponent(orgSlug)}`
    );
  },

  async getProfileBySubdomain(label: string): Promise<ApiResponse<PublicOrganizationProfile>> {
    return publicApiGet<PublicOrganizationProfile>(
      `/public/academies/subdomain/${encodeURIComponent(label)}`
    );
  },

  /** Resolves org slug from a legacy academy slug via thin academy wrapper. */
  async resolveOrgSlugFromAcademySlug(academySlug: string): Promise<string | null> {
    const res = await publicApiGet<PublicOrganizationProfile>(
      `/public/academies/${encodeURIComponent(academySlug)}`
    );
    if (!res.success || !res.data) return null;
    return res.data.organization.slug;
  },

  /** Resolves org slug from a legacy club slug via thin club wrapper. */
  async resolveOrgSlugFromClubSlug(clubSlug: string): Promise<string | null> {
    const res = await publicApiGet<PublicOrganizationProfile & { club: { slug: string } }>(
      `/public/clubs/${encodeURIComponent(clubSlug)}`
    );
    if (!res.success || !res.data) return null;
    return res.data.organization.slug;
  },

  async getNearby(orgSlug: string, limit = 8): Promise<ApiResponse<PublicDirectoryEntry[]>> {
    const qs = limit !== 8 ? `?limit=${limit}` : '';
    return publicApiGet<PublicDirectoryEntry[]>(
      `/public/orgs/${encodeURIComponent(orgSlug)}/nearby${qs}`
    );
  },

  async getCourtHireAvailabilityGrid(
    orgSlug: string,
    query: CourtHireAvailabilityQuery
  ): Promise<ApiResponse<CourtHireAvailabilityGrid>> {
    const params = new URLSearchParams({ date: query.date });
    if (query.sportId) params.set('sportId', query.sportId);
    if (query.locationId) params.set('locationId', query.locationId);
    return publicApiGet<CourtHireAvailabilityGrid>(
      `/public/orgs/${encodeURIComponent(orgSlug)}/court-hire/availability?${params.toString()}`
    );
  },
};
